'use client'

import type { DialogAPI } from '@fukku/dialog/Dialog'
import {
	createContext,
	type ReactNode,
	type RefObject,
	useContext,
	useMemo,
	useRef,
	useState,
} from 'react'
import type { Media } from '../../../../types/FullProduct'

export interface ZoomContextType {
	setZoomImage: (image: Media | null) => void
	zoomImage: Media | null
	zoomDialogRef: RefObject<DialogAPI | null>
	setIsDialogOpen: (boolean: boolean) => void
	isDialogOpen: boolean
}

type ZoomProviderProps = {
	readonly children: ReactNode
}

export const ZoomContext = createContext<ZoomContextType | undefined>(undefined)

export function ZoomProvider({ children }: ZoomProviderProps): ReactNode {
	const zoomDialogRef = useRef<DialogAPI>(null)
	const [zoomImage, setZoomImage] = useState<Media | null>(null)
	const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false)

	const value = useMemo(
		() => ({
			setZoomImage,
			zoomImage,
			zoomDialogRef,
			setIsDialogOpen,
			isDialogOpen,
		}),
		[zoomImage, isDialogOpen]
	)

	return <ZoomContext.Provider value={value}>{children}</ZoomContext.Provider>
}

/** @throws {Error} If useZoom is used without ZoomProvider */
export const useZoom = (): ZoomContextType => {
	const context = useContext(ZoomContext)
	if (!context) {
		throw new Error('useZoom must be used within a ZoomProvider')
	}
	return context
}
